import { fetchWithToken } from '@/services/fetcher'
import fetcher from '@/services/fetcher'

// ==================== Facebook Cookie ====================

/**
 * GraphQL mutation để update Facebook cookie
 */
const UPDATE_FACEBOOK_COOKIE = `
  mutation updateFacebookCookie($fbc: String, $fbp: String) {
    updateFacebookCookie(fbc: $fbc, fbp: $fbp)
  }
`

/**
 * Update Facebook cookie lên server
 * @param fbCookie - Object chứa _fbp và _fbc
 * @returns Response với status và data
 */
export const updateFacebookCookie = async (
  fbCookie: IFacebookCookie
): Promise<IUpdateFacebookCookieResponse | null> => {
  const { _fbp, _fbc } = fbCookie

  // Validate data trước khi call API
  if (!_fbp || !_fbc) {
    console.warn('Facebook cookies _fbp or _fbc not found')
    return null
  }

  try {
    const response = await fetchWithToken({
      query: UPDATE_FACEBOOK_COOKIE,
      variables: { fbc: _fbc, fbp: _fbp },
    })

    // Xử lý GraphQL errors
    if (response?.payload?.errors) {
      return {
        status: 'error',
        message: response.payload.errors[0]?.message,
      }
    }

    const data = response?.payload?.data?.updateFacebookCookie

    if (!data) return null

    return {
      status: 'success',
      data,
    }
  } catch (error) {
    console.error('Error updating Facebook cookie:', error)
    throw new Error(JSON.stringify(error))
  }
}

// ==================== Referral Tracking ====================

/**
 * GraphQL mutation để log action
 */
const LOG_ACTION = `
  mutation logAction($type: String!, $title: String!, $additional_data: String!) {
    saveActionLog(type: $type, title: $title, additional_data: $additional_data)
  }
`

/**
 * Log action returning với referral data
 * @param referralData - JSON string chứa thông tin referral
 * @returns Response với status và data
 */
export const saveActionLog = async (payload: {
  productId?: number | null
  type: string
  title: string
  additional_data: string | null
}) => {
  try {
    const response = await fetchWithToken({
      query: LOG_ACTION,
      variables: payload,
    })

    const data = response?.payload?.data?.saveActionLog
    if (response?.payload?.errors) {
      return { status: 'error', message: response.payload.errors[0]?.message }
    }

    if (!data) return null
    return { status: 'success', data }
  } catch (error) {
    console.error('Error saving action log:', error)
    throw new Error(JSON.stringify(error))
  }
}

// ==================== Facebook PageView Tracking ====================

/**
 * GraphQL mutation để gửi Facebook PageView event
 */
const SEND_FACEBOOK_PAGE_VIEW = `
  mutation sendFacebookPageView {
    sendFacebookPageView
  }
`

/**
 * Gửi Facebook PageView event lên server
 * Call mỗi khi user load/refresh trang web (không call khi chuyển route)
 * @returns Response với status và data
 */
export const sendFacebookPageView = async (): Promise<{
  status: string
  data?: boolean
  message?: string
} | null> => {
  try {
    const response = await fetchWithToken({
      query: SEND_FACEBOOK_PAGE_VIEW,
      variables: {},
    })

    // Xử lý GraphQL errors
    if (response?.payload?.errors) {
      return {
        status: 'error',
        message: response.payload.errors[0]?.message,
      }
    }

    const data = response?.payload?.data?.sendFacebookPageView

    if (data === undefined || data === null) return null

    return {
      status: 'success',
      data,
    }
  } catch (error) {
    console.error('Error sending Facebook PageView:', error)
    return {
      status: 'error',
      message: 'Failed to send Facebook PageView',
    }
  }
}
